import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../stores/auth';
import type { Ciudadano } from '../stores/auth';

// ── Tipos ─────────────────────────────────────────────────────
type Municipio = { id: number; nombre: string; localidad: string; provincia: string; codigo_postal: string };
type Barrio    = { id: number; nombre: string };
type Captcha   = { token: string; pregunta: string };
// file = original File; objUrl = blob URL para preview; rotation = ángulo CSS acumulado
type FotoState = { file: File; objUrl: string; rotation: number };

// ── Helpers ───────────────────────────────────────────────────
// Comprime y rota una foto antes de subir. Devuelve un Blob JPEG.
function fotoToBlob(foto: FotoState, maxPx = 1200): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let w = img.naturalWidth, h = img.naturalHeight;
      if (w > maxPx || h > maxPx) {
        if (w >= h) { h = Math.round(h * maxPx / w); w = maxPx; }
        else        { w = Math.round(w * maxPx / h); h = maxPx; }
      }
      const deg      = foto.rotation;
      const swapAxes = deg % 180 !== 0;
      const cw = swapAxes ? h : w;
      const ch = swapAxes ? w : h;
      const canvas = document.createElement('canvas');
      canvas.width  = cw;
      canvas.height = ch;
      const ctx = canvas.getContext('2d')!;
      ctx.translate(cw / 2, ch / 2);
      ctx.rotate((deg * Math.PI) / 180);
      ctx.drawImage(img, -w / 2, -h / 2, w, h);
      canvas.toBlob(
        blob => blob ? resolve(blob) : reject(new Error('canvas vacío')),
        'image/jpeg', 0.75
      );
    };
    img.onerror = () => reject(new Error('no se pudo cargar la imagen'));
    img.src = foto.objUrl;
  });
}

// Validación CUIL/CUIT — algoritmo dígito verificador ARCA (ex AFIP)
function validarCuil(raw: string): boolean {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 11) return false;
  const mult = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const suma = mult.reduce((acc, m, i) => acc + m * +d[i], 0);
  const resto = suma % 11;
  const verif = resto === 0 ? 0 : resto === 1 ? -1 : 11 - resto;
  return verif !== -1 && verif === +d[10];
}

// Calcula CUIL automáticamente desde DNI argentino (7-8 dígitos) + género
// M → prefijo 20, F → 27, otro → 23. Si resto==1 reintenta con prefijo 23.
function calcularCuil(nroDni: string, genero: string): string | null {
  const d = nroDni.replace(/\D/g, '');
  if (d.length < 7 || d.length > 8) return null;
  const dniPad = d.padStart(8, '0');
  const mult   = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const intentar = (pref: string): string | null => {
    const digits = (pref + dniPad).split('').map(Number);
    const suma   = digits.reduce((s, x, i) => s + x * mult[i], 0);
    const resto  = suma % 11;
    if (resto === 1) return null;
    return `${pref}-${dniPad}-${resto === 0 ? 0 : 11 - resto}`;
  };
  const pref = genero === 'F' ? '27' : genero === 'M' ? '20' : '23';
  return intentar(pref) ?? intentar('23');
}

const TIPOS_DOC = ['DNI', 'LE', 'LC', 'PASAPORTE', 'EXT'] as const;
type TipoDoc = typeof TIPOS_DOC[number];
const SIN_CUIL: TipoDoc[] = ['EXT', 'PASAPORTE'];
const INPUT      = 'w-full rounded-xl px-4 py-3 text-sm outline-none';
const INPUT_STYLE = { backgroundColor: '#ffffff', border: '1px solid #6e6b6b', color: '#303030' };
const LABEL      = 'block text-xs font-semibold mb-1.5 text-slate-500';

// ── Componente ────────────────────────────────────────────────
export default function Register() {
  const nav        = useNavigate();
  const setSession = useAuth((s) => s.setSession);

  // Form
  const [form, setForm] = useState({
    nombre: '', apellido: '', tipo_documento: 'DNI', nro_documento: '', genero: '',
    nro_tramite: '', cuil: '',
    telefono: '', email: '',
    calle: '', nro_puerta: '', piso_depto: '', barrio: '', barrio_id: '' as string | number,
    entre_calles: '', codigo_postal: '',
    password: '', confirm_pass: '',
  });
  const set = (k: string, v: string | number) => setForm(f => ({ ...f, [k]: v }));

  // Municipio
  const [municipio, setMunicipio] = useState<Municipio | null>(null);
  const [cpError,   setCpError]   = useState('');
  const [barrios,   setBarrios]   = useState<Barrio[]>([]);

  // Fotos — preview instantáneo vía object URL, rotación por CSS
  const [fotoAnverso, setFotoAnverso] = useState<FotoState | null>(null);
  const [fotoReverso, setFotoReverso] = useState<FotoState | null>(null);


  // Captcha
  const [captcha,    setCaptcha]    = useState<Captcha | null>(null);
  const [captchaResp, setCaptchaResp] = useState('');
  const [captchaLoad, setCaptchaLoad] = useState(false);

  // UI
  const [showPass, setShowPass] = useState(false);
  const [acepto,   setAcepto]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState<string | null>(null);

  // ── CUIL auto-cálculo ──────────────────────────────────────
  // Para DNI argentino calcula el CUIL automáticamente cuando el usuario
  // completa el nro. de documento y selecciona el género.
  useEffect(() => {
    if (form.tipo_documento === 'DNI' && form.genero && form.nro_documento.length >= 7) {
      const cuil = calcularCuil(form.nro_documento, form.genero);
      if (cuil) set('cuil', cuil);
    } else if ((SIN_CUIL as string[]).includes(form.tipo_documento)) {
      set('cuil', '');
    }
  }, [form.tipo_documento, form.nro_documento, form.genero]);

  // ── Captcha ────────────────────────────────────────────────
  async function cargarCaptcha() {
    setCaptchaLoad(true);
    setCaptchaResp('');
    try {
      const d = await api<Captcha>('/auth?action=captcha', { auth: false });
      setCaptcha(d);
    } catch { /* silencioso */ } finally { setCaptchaLoad(false); }
  }
  useEffect(() => { cargarCaptcha(); }, []);

  // ── CP → Municipio ─────────────────────────────────────────
  async function onCpChange(cp: string) {
    set('codigo_postal', cp);
    setCpError('');
    setMunicipio(null);
    setBarrios([]);
    set('barrio', '');
    set('barrio_id', '');
    if (cp.length === 4) {
      try {
        const lista = await api<Municipio[]>('/municipios?action=lista', { auth: false });
        const m = lista.find(x => String(x.codigo_postal) === cp);
        if (m) {
          setMunicipio(m);
          try {
            const bs = await api<Barrio[]>(`/barrios?municipio_id=${m.id}`, { auth: false });
            setBarrios(bs);
          } catch { /* sin barrios */ }
        } else {
          setCpError('Código postal no habilitado en la app.');
        }
      } catch { setCpError('No se pudo verificar el municipio. Revisá tu conexión.'); }
    }
  }

  // ── Foto: selección instantánea con FileReader (data URL inline) ──
  function onFoto(e: React.ChangeEvent<HTMLInputElement>, lado: 'anverso' | 'reverso') {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { setErr('La foto no puede superar 25 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const setter = lado === 'anverso' ? setFotoAnverso : setFotoReverso;
      setter({ file, objUrl: dataUrl, rotation: 0 });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  // Rotación: solo actualiza el ángulo — el canvas solo se aplica al enviar
  function rotarFoto(lado: 'anverso' | 'reverso') {
    const setter = lado === 'anverso' ? setFotoAnverso : setFotoReverso;
    setter(prev => prev ? { ...prev, rotation: (prev.rotation + 90) % 360 } : null);
  }

  // ── Submit ─────────────────────────────────────────────────
  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);

    if (!municipio)                          return setErr('Ingresá un código postal válido');
    if (!form.nombre.trim())                 return setErr('Ingresá tu nombre');
    if (!form.apellido.trim())               return setErr('Ingresá tu apellido');
    if (!form.nro_documento.trim())          return setErr('Ingresá tu documento');
    if (!form.genero)                        return setErr('Seleccioná tu sexo');
    if (!form.telefono.trim())               return setErr('Ingresá tu teléfono');
    if (!form.calle.trim())                  return setErr('Ingresá tu calle');
    if (!form.password)                      return setErr('Ingresá una contraseña');
    if (form.password.length < 6)            return setErr('La contraseña requiere mínimo 6 caracteres');
    if (form.password !== form.confirm_pass) return setErr('Las contraseñas no coinciden');
    if (form.email && !/\S+@\S+\.\S+/.test(form.email)) return setErr('Email inválido');
    if (!fotoAnverso)                        return setErr('Subí la foto del frente del documento');
    if (!fotoReverso)                        return setErr('Subí la foto del dorso del documento');
    if (!captcha)                            return setErr('Cargando verificación, esperá un momento');
    if (!captchaResp.trim())                 return setErr('Completá la verificación de seguridad');
    if (!acepto)                             return setErr('Debés aceptar la Política de Privacidad y los Términos');

    const cuil = form.cuil.replace(/\D/g, '');
    const tieneCuil = !(SIN_CUIL as string[]).includes(form.tipo_documento);
    if (tieneCuil && cuil && cuil.length !== 11) return setErr('CUIL/CUIT inválido — debe tener 11 dígitos');
    if (tieneCuil && cuil && !validarCuil(cuil))  return setErr('CUIL/CUIT inválido — el dígito verificador no coincide');

    const nroTramite = form.nro_tramite.replace(/\D/g, '');
    if (nroTramite && (nroTramite.length < 9 || nroTramite.length > 12)) {
      return setErr('Número de trámite inválido — debe tener entre 9 y 12 dígitos');
    }

    setLoading(true);
    try {
      // Comprimir y rotar las fotos vía Canvas — solo en el momento del envío
      let blobAnverso: Blob, blobReverso: Blob;
      try {
        [blobAnverso, blobReverso] = await Promise.all([
          fotoToBlob(fotoAnverso),
          fotoToBlob(fotoReverso),
        ]);
      } catch {
        setLoading(false);
        return setErr('No se pudo procesar las fotos. Probá con otra imagen.');
      }

      // Enviar como multipart/form-data — evita límites de tamaño de JSON
      const fd = new FormData();
      fd.append('nombre',         form.nombre.trim());
      fd.append('apellido',       form.apellido.trim());
      fd.append('tipo_documento', form.tipo_documento);
      fd.append('nro_documento',  form.nro_documento.trim());
      fd.append('genero',         form.genero);
      if (nroTramite) fd.append('nro_tramite', nroTramite);
      if (cuil)       fd.append('cuil', cuil);
      fd.append('telefono',       form.telefono.trim());
      if (form.email.trim())      fd.append('email', form.email.trim());
      fd.append('calle',          form.calle.trim());
      if (form.nro_puerta.trim()) fd.append('nro_puerta', form.nro_puerta.trim());
      if (form.piso_depto.trim()) fd.append('piso_depto', form.piso_depto.trim());
      if (typeof form.barrio_id === 'number') {
        fd.append('barrio_id', String(form.barrio_id));
      } else if (form.barrio.trim()) {
        fd.append('barrio', form.barrio.trim());
      }
      if (form.entre_calles.trim()) fd.append('entre_calles', form.entre_calles.trim());
      fd.append('codigo_postal',  form.codigo_postal);
      fd.append('municipio_id',   String(municipio.id));
      fd.append('password',       form.password);
      fd.append('captcha_token',     captcha.token);
      fd.append('captcha_respuesta', captchaResp.trim());
      fd.append('foto_anverso', blobAnverso, 'anverso.jpg');
      fd.append('foto_reverso', blobReverso, 'reverso.jpg');

      const data = await api<{ access: string; refresh: string; ciudadano: Ciudadano }>(
        '/auth?action=register',
        { method: 'POST', body: fd, auth: false }
      );
      setSession(data.access, data.refresh, data.ciudadano);
      nav('/');
    } catch (e: any) {
      setErr(e.message || 'Error al registrarse');
      if (e.message?.toLowerCase().includes('captcha') || e.message?.toLowerCase().includes('verificaci')) {
        cargarCaptcha();
      }
    } finally {
      setLoading(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────
  return (
    <main className="min-h-screen flex flex-col items-center justify-start px-4 py-8" style={{ backgroundColor: '#f2f2f2' }}>
      <div className="w-full max-w-sm">

        {/* Header */}
        <div className="flex flex-col items-center mb-6">
          <img src={`${import.meta.env.BASE_URL}icon.png`} alt="CiudadApp" className="w-16 h-16 rounded-2xl mb-3 shadow-md" />
          <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">Crear cuenta</h1>
          <p className="text-gray-500 text-xs mt-1">Registrate para acceder a los servicios municipales</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">

          {/* ── MUNICIPIO ───────────────────────────────────── */}
          <Section title="📍 Tu municipio">
            <div>
              <label className={LABEL}>Código postal *</label>
              <input
                type="text" inputMode="numeric" maxLength={4}
                value={form.codigo_postal}
                onChange={e => onCpChange(e.target.value.replace(/\D/g, ''))}
                placeholder="Ej: 8340"
                className={INPUT} style={INPUT_STYLE}
              />
              {cpError && <p className="text-red-500 text-xs mt-1">{cpError}</p>}
              {municipio && (
                <p className="text-green-700 text-xs mt-1 font-semibold">
                  ✅ {municipio.nombre} — {municipio.localidad}, {municipio.provincia}
                </p>
              )}
            </div>
          </Section>

          {municipio && (
            <>
              {/* ── DATOS PERSONALES ──────────────────────── */}
              <Section title="👤 Datos personales">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL}>Nombre *</label>
                    <input className={INPUT} style={INPUT_STYLE} placeholder="Juan"
                      value={form.nombre} onChange={e => set('nombre', e.target.value)} />
                  </div>
                  <div>
                    <label className={LABEL}>Apellido *</label>
                    <input className={INPUT} style={INPUT_STYLE} placeholder="Pérez"
                      value={form.apellido} onChange={e => set('apellido', e.target.value)} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL}>Tipo doc.</label>
                    <select className={INPUT} style={INPUT_STYLE}
                      value={form.tipo_documento} onChange={e => set('tipo_documento', e.target.value)}>
                      <option value="DNI">DNI</option>
                      <option value="LE">LE (Libreta Enrolamiento)</option>
                      <option value="LC">LC (Libreta Cívica)</option>
                      <option value="PASAPORTE">Pasaporte</option>
                      <option value="EXT">Extranjero/a</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL}>Nro. documento *</label>
                    <input className={INPUT} style={INPUT_STYLE}
                      inputMode={['EXT', 'PASAPORTE'].includes(form.tipo_documento) ? 'text' : 'numeric'}
                      placeholder={form.tipo_documento === 'PASAPORTE' ? 'AAA123456' : form.tipo_documento === 'EXT' ? 'CE-12345678' : '30123456'}
                      maxLength={15}
                      value={form.nro_documento}
                      onChange={e => {
                        const v = ['EXT', 'PASAPORTE'].includes(form.tipo_documento)
                          ? e.target.value.toUpperCase().replace(/[^A-Z0-9\-]/g, '')
                          : e.target.value.replace(/\D/g, '');
                        set('nro_documento', v);
                      }} />
                  </div>
                </div>

                <div>
                  <label className={LABEL}>Sexo *</label>
                  <select className={INPUT} style={INPUT_STYLE}
                    value={form.genero} onChange={e => set('genero', e.target.value)}>
                    <option value="">Seleccioná...</option>
                    <option value="M">Masculino</option>
                    <option value="F">Femenino</option>
                    <option value="X">No binario / X</option>
                  </select>
                  {form.tipo_documento === 'DNI' && (
                    <p className="text-xs text-slate-400 mt-1">
                      En Argentina el mismo número de DNI puede estar asignado a personas de distinto sexo.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {form.tipo_documento === 'DNI' && (
                    <div>
                      <label className={LABEL}>Nro. de trámite</label>
                      <input className={INPUT} style={INPUT_STYLE} inputMode="numeric"
                        placeholder="00123456789" maxLength={12}
                        value={form.nro_tramite}
                        onChange={e => set('nro_tramite', e.target.value.replace(/\D/g, ''))} />
                      <p className="text-xs text-slate-400 mt-1">Dorso del DNI</p>
                    </div>
                  )}
                  {!(SIN_CUIL as string[]).includes(form.tipo_documento) && (
                    <div>
                      <label className={LABEL}>
                        {form.tipo_documento === 'DNI' ? 'CUIL (auto-calculado)' : 'CUIL / CUIT (opcional)'}
                      </label>
                      <input className={INPUT}
                        style={form.tipo_documento === 'DNI'
                          ? { ...INPUT_STYLE, backgroundColor: '#f5f5f5', color: '#555' }
                          : INPUT_STYLE}
                        inputMode="numeric" placeholder="20-12345678-9" maxLength={13}
                        readOnly={form.tipo_documento === 'DNI'}
                        value={form.cuil}
                        onChange={e => form.tipo_documento !== 'DNI' && set('cuil', e.target.value.replace(/[^\d-]/g, ''))}
                      />
                      {form.tipo_documento === 'DNI' && form.cuil
                        ? <p className="text-xs text-green-600 mt-1 font-semibold">✓ Calculado automáticamente</p>
                        : (() => {
                            const d = form.cuil.replace(/\D/g, '');
                            if (!d) return null;
                            if (d.length < 11) return <p className="text-xs text-slate-400 mt-1">{d.length}/11 dígitos</p>;
                            return validarCuil(d)
                              ? <p className="text-xs text-green-600 mt-1 font-semibold">✓ CUIL/CUIT válido</p>
                              : <p className="text-xs text-red-500 mt-1">✗ Dígito verificador incorrecto</p>;
                          })()}
                    </div>
                  )}
                </div>

                <div>
                  <label className={LABEL}>Teléfono *</label>
                  <input className={INPUT} style={INPUT_STYLE} inputMode="tel"
                    placeholder="2984-123456"
                    value={form.telefono} onChange={e => set('telefono', e.target.value)} />
                </div>

                <div>
                  <label className={LABEL}>Email (opcional)</label>
                  <input className={INPUT} style={INPUT_STYLE} type="email" inputMode="email"
                    placeholder="juan@mail.com" autoComplete="email"
                    value={form.email} onChange={e => set('email', e.target.value)} />
                </div>
              </Section>

              {/* ── DOMICILIO ─────────────────────────────── */}
              <Section title="🏠 Domicilio">
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className={LABEL}>Calle *</label>
                    <input className={INPUT} style={INPUT_STYLE} placeholder="Av. San Martín"
                      value={form.calle} onChange={e => set('calle', e.target.value)} />
                  </div>
                  <div>
                    <label className={LABEL}>Nro.</label>
                    <input className={INPUT} style={INPUT_STYLE} inputMode="numeric"
                      placeholder="123"
                      value={form.nro_puerta} onChange={e => set('nro_puerta', e.target.value)} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL}>Piso / Depto</label>
                    <input className={INPUT} style={INPUT_STYLE} placeholder="3°B"
                      value={form.piso_depto} onChange={e => set('piso_depto', e.target.value)} />
                  </div>
                  <div>
                    <label className={LABEL}>Entre calles</label>
                    <input className={INPUT} style={INPUT_STYLE} placeholder="Rivadavia y Mitre"
                      value={form.entre_calles} onChange={e => set('entre_calles', e.target.value)} />
                  </div>
                </div>

                <div>
                  <label className={LABEL}>Barrio *</label>
                  {barrios.length > 0 ? (
                    <select className={INPUT} style={INPUT_STYLE}
                      value={form.barrio_id}
                      onChange={e => {
                        const b = barrios.find(x => x.id === +e.target.value);
                        set('barrio_id', +e.target.value);
                        set('barrio', b?.nombre ?? '');
                      }}>
                      <option value="">Seleccioná tu barrio...</option>
                      {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                    </select>
                  ) : (
                    <input className={INPUT} style={INPUT_STYLE} placeholder="Centro"
                      value={form.barrio} onChange={e => set('barrio', e.target.value)} />
                  )}
                </div>
              </Section>

              {/* ── FOTO DNI ──────────────────────────────── */}
              <Section title="📷 Foto del documento">
                <p className="text-xs text-slate-500 -mt-1 mb-3">
                  Se usan para verificar tu identidad. Solo las ve el personal municipal autorizado.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <FotoSlot label="Frente *" foto={fotoAnverso}
                    onChange={e => onFoto(e, 'anverso')}
                    onRotate={() => rotarFoto('anverso')} />
                  <FotoSlot label="Dorso *" foto={fotoReverso}
                    onChange={e => onFoto(e, 'reverso')}
                    onRotate={() => rotarFoto('reverso')} />
                </div>
              </Section>

              {/* ── CONTRASEÑA ────────────────────────────── */}
              <Section title="🔑 Contraseña">
                <div>
                  <label className={LABEL}>Contraseña *</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      className={INPUT} style={INPUT_STYLE}
                      placeholder="Mínimo 6 caracteres" autoComplete="new-password"
                      value={form.password} onChange={e => set('password', e.target.value)}
                    />
                    <button type="button" tabIndex={-1}
                      onClick={() => setShowPass(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xl">
                      {showPass ? '🐵' : '🙈'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={LABEL}>Repetir contraseña *</label>
                  <input
                    type={showPass ? 'text' : 'password'}
                    className={INPUT} style={INPUT_STYLE}
                    placeholder="••••••••" autoComplete="new-password"
                    value={form.confirm_pass} onChange={e => set('confirm_pass', e.target.value)}
                  />
                </div>
              </Section>

              {/* ── CAPTCHA ───────────────────────────────── */}
              <Section title="🤖 Verificación de seguridad">
                {captchaLoad ? (
                  <p className="text-sm text-slate-500">Cargando verificación...</p>
                ) : captcha ? (
                  <div className="flex gap-3 items-center">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800 mb-1.5">{captcha.pregunta}</p>
                      <input
                        className={INPUT} style={INPUT_STYLE}
                        inputMode="numeric" maxLength={4} placeholder="Tu respuesta"
                        value={captchaResp} onChange={e => setCaptchaResp(e.target.value.replace(/\D/g, ''))}
                      />
                    </div>
                    <button type="button" onClick={cargarCaptcha}
                      className="mt-6 text-2xl" title="Refrescar">🔄</button>
                  </div>
                ) : (
                  <button type="button" onClick={cargarCaptcha}
                    className="text-brand-600 text-sm font-medium">Cargar verificación →</button>
                )}
              </Section>

              {/* ── TÉRMINOS ──────────────────────────────── */}
              <label className="flex items-start gap-3 cursor-pointer">
                <div
                  onClick={() => setAcepto(v => !v)}
                  className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 border-2 flex items-center justify-center ${acepto ? 'bg-brand-600 border-brand-600' : 'border-gray-400 bg-white'}`}>
                  {acepto && <span className="text-white text-xs font-black">✓</span>}
                </div>
                <span className="text-xs text-gray-600 leading-relaxed">
                  Acepto la{' '}
                  <a href="https://www.ciudadapp.com.ar/politica-privacidad.html" target="_blank"
                    className="text-brand-600 font-semibold underline" rel="noreferrer">
                    Política de Privacidad
                  </a>
                  {' '}y los{' '}
                  <a href="https://www.ciudadapp.com.ar/terminos-condiciones.html" target="_blank"
                    className="text-brand-600 font-semibold underline" rel="noreferrer">
                    Términos y Condiciones
                  </a>
                  . Los datos se tratarán conforme a la Ley 25.326.
                </span>
              </label>

              {err && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl p-3">{err}</div>
              )}

              <button
                type="submit" disabled={loading || !acepto}
                className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white rounded-xl py-3.5 font-bold text-base">
                {loading ? 'Creando cuenta…' : '✅ Crear mi cuenta'}
              </button>
            </>
          )}
        </form>

        <button onClick={() => nav('/login')}
          className="w-full mt-5 text-center text-gray-500 text-sm py-3">
          ← Ya tengo cuenta, iniciá sesión
        </button>

      </div>
    </main>
  );
}

// ── Sub-componentes ───────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl p-5 shadow-sm space-y-3" style={{ backgroundColor: '#ffffff', border: '1px solid #6e6b6b' }}>
      <p className="font-bold text-gray-700 text-xs uppercase tracking-wider mb-1">{title}</p>
      {children}
    </div>
  );
}

function FotoSlot({
  label, foto, onChange, onRotate,
}: {
  label: string;
  foto: FotoState | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRotate: () => void;
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-1.5">{label}</p>

      {/* Input invisible superpuesto — el usuario toca directamente el input,
          sin label/htmlFor. Más confiable en todos los browsers móviles.     */}
      <div
        className="relative w-full h-28 rounded-xl overflow-hidden flex items-center justify-center"
        style={{ border: `1.5px ${foto ? 'solid #32329f' : 'dashed #6e6b6b'}`, background: '#f8fafc' }}>
        {foto ? (
          <img
            src={foto.objUrl}
            alt=""
            className="w-full h-full object-contain"
            style={{ transform: `rotate(${foto.rotation}deg)`, transition: 'transform 0.2s' }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center pointer-events-none">
            <span className="text-3xl">🪪</span>
            <span className="text-xs text-slate-500 mt-1">Subir foto</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onChange}
          className="absolute inset-0 opacity-0 cursor-pointer"
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Botones acción — solo visibles cuando hay foto */}
      {foto && (
        <div className="flex gap-1 mt-1.5">
          <button type="button" onClick={onRotate}
            className="flex-1 text-xs rounded-lg py-1.5 font-semibold"
            style={{ background: '#e8e8f5', color: '#32329f' }}>
            ↻ Girar
          </button>
          <button type="button" onClick={() => inputRef.current?.click()}
            className="flex-1 text-xs rounded-lg py-1.5 font-semibold"
            style={{ background: '#f0f0f0', color: '#555' }}>
            Cambiar
          </button>
        </div>
      )}
    </div>
  );
}
